import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { apiService, PackAttachable } from '@/services/api.service';
import { buildGeometryMesh, loadPackImage } from './BedrockModelViewer';
import { armorGeometry, detectArmorSlot, loadMannequinSkin, playerGeometry } from './playerModel';
import styles from './BedrockModelViewer.module.scss';

interface PlayerPreviewProps {
  groupId: string;
  identifier: string;
  iconPath?: string | null;
  attachable?: PackAttachable | null;
}

// Transforms du hold (perso face +Z, main droite en -X ; la main est à y=0.72). Réglables à l'œil.
// 3e personne : monde, item tenu dans le poing, lame vers le haut/avant, face large vers l'avant-droit.
const HOLD3 = { pos: [-0.42, 0.72, 0.1] as const, rot: [0.3, -0.5, 0.2] as const, off: [0, 0.08, 0] as const, size: 0.6 };
// 1ère personne : repère caméra (regard vers -Z ; droite = +X, haut = +Y). Bas-droite, incliné, face à la caméra.
const HOLDFP = { pos: [0.3, -0.32, -0.72] as const, rot: [0.15, -0.35, 0.5] as const, size: 0.75 };

function disposeObject3D(obj: THREE.Object3D | null) {
  if (!obj) return;
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach((m) => { (m as any).map?.dispose(); m.dispose(); });
    else if (mat) { (mat as any).map?.dispose(); mat.dispose(); }
  });
}

function iconTexture(icon: HTMLImageElement): THREE.Texture {
  const tex = new THREE.Texture(icon);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function flatPlane(tex: THREE.Texture, size: number, off?: readonly [number, number, number]): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(size, size),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.5, side: THREE.DoubleSide }),
  );
  if (off) m.position.set(off[0], off[1], off[2]);
  return m;
}

/** Aperçu joueur : armure équipée (si l'item est une armure) ou item tenu en main, en 3e personne + vision joueur. */
export function PlayerPreview({ groupId, identifier, iconPath, attachable }: PlayerPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const camPovRef = useRef<THREE.PerspectiveCamera | null>(null);
  const modelRef = useRef<THREE.Object3D | null>(null);
  const headRef = useRef<THREE.Object3D | null>(null);
  const heldThirdRef = useRef<THREE.Object3D | null>(null); // item tenu (monde, 3e personne)
  const heldFpRef = useRef<THREE.Object3D | null>(null); // item tenu (attaché à la caméra, 1ère personne)
  // Vue 3/4 par défaut (côté droit du perso) pour voir la face large de l'item tenu.
  const camRef = useRef({ theta: -0.55, phi: 1.3, radius: 3.2, dragging: false });
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // --- Scène + double viewport (montée une fois) ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setScissorTest(true);
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const dir = new THREE.DirectionalLight(0xffffff, 0.85);
    dir.position.set(4, 8, 6);
    scene.add(dir);

    const target = new THREE.Vector3(0, 1.0, 0); // torse
    const cam3 = new THREE.PerspectiveCamera(45, 1, 0.05, 100);
    const camPov = new THREE.PerspectiveCamera(70, 1, 0.02, 100);
    camPovRef.current = camPov;
    scene.add(camPov); // dans le graphe pour que l'item 1ère personne (enfant) soit rendu

    const applyCam3 = () => {
      const c = camRef.current;
      cam3.position.set(
        target.x + c.radius * Math.sin(c.phi) * Math.sin(c.theta),
        target.y + c.radius * Math.cos(c.phi),
        target.z + c.radius * Math.sin(c.phi) * Math.cos(c.theta),
      );
      cam3.lookAt(target);
    };
    const applyCamPov = () => {
      // Yeux du joueur, regard vers l'avant (+Z) légèrement vers le bas (tête masquée au rendu).
      camPov.position.set(0, 1.42, 0.1);
      camPov.lookAt(0, 1.05, 1.5);
    };

    const sizeOf = () => {
      const w = container.clientWidth || 480;
      const h = container.clientHeight || 240;
      renderer.setSize(w, h, false);
    };
    sizeOf();

    const renderView = (cam: THREE.PerspectiveCamera, x: number, y: number, w: number, h: number) => {
      renderer.setViewport(x, y, w, h);
      renderer.setScissor(x, y, w, h);
      cam.aspect = w / Math.max(1, h);
      cam.updateProjectionMatrix();
      renderer.render(scene, cam);
    };

    const showHeld = (third: boolean) => {
      if (heldThirdRef.current) heldThirdRef.current.visible = third;
      if (heldFpRef.current) heldFpRef.current.visible = !third;
    };

    let raf = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      applyCam3();
      applyCamPov();
      const bw = renderer.domElement.width;
      const bh = renderer.domElement.height;
      const half = Math.floor(bw / 2);
      // Gauche : 3e personne (tête + item tenu à la main visibles).
      if (headRef.current) headRef.current.visible = true;
      showHeld(true);
      renderView(cam3, 0, 0, half, bh);
      // Droite : vision joueur (tête masquée, item 1ère personne à la caméra).
      if (headRef.current) headRef.current.visible = false;
      showHeld(false);
      renderView(camPov, half, 0, bw - half, bh);
      if (headRef.current) headRef.current.visible = true;
    };
    animate();

    // Orbite (3e personne) au drag ; zoom molette.
    let px = 0;
    let py = 0;
    const onDown = (e: PointerEvent) => {
      camRef.current.dragging = true;
      px = e.clientX;
      py = e.clientY;
      canvas.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!camRef.current.dragging) return;
      const c = camRef.current;
      c.theta -= (e.clientX - px) * 0.01;
      c.phi = Math.max(0.15, Math.min(Math.PI - 0.15, c.phi + (e.clientY - py) * 0.01));
      px = e.clientX;
      py = e.clientY;
    };
    const onUp = () => { camRef.current.dragging = false; };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const c = camRef.current;
      c.radius = Math.max(1.8, Math.min(7, c.radius * (e.deltaY > 0 ? 1.1 : 0.9)));
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    const ro = new ResizeObserver(sizeOf);
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('wheel', onWheel);
      if (heldFpRef.current) { camPov.remove(heldFpRef.current); disposeObject3D(heldFpRef.current); heldFpRef.current = null; }
      disposeObject3D(modelRef.current);
      modelRef.current = null;
      renderer.dispose();
      sceneRef.current = null;
      camPovRef.current = null;
    };
  }, []);

  // --- (Re)construction du modèle quand l'item change ---
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    (async () => {
      const scene = sceneRef.current;
      if (!scene) return;
      // Retire l'ancien modèle + l'item 1ère personne (attaché à la caméra).
      if (modelRef.current) { scene.remove(modelRef.current); disposeObject3D(modelRef.current); modelRef.current = null; }
      if (heldFpRef.current) { camPovRef.current?.remove(heldFpRef.current); disposeObject3D(heldFpRef.current); heldFpRef.current = null; }
      heldThirdRef.current = null;

      const skin = await loadMannequinSkin();
      if (cancelled || !skin) { if (!cancelled) setStatus('error'); return; }

      const root = new THREE.Group();
      const player = buildGeometryMesh(playerGeometry(), skin, undefined, false);
      headRef.current = player.getObjectByName('head') ?? null;
      root.add(player);

      // Ajoute un item tenu : une copie dans la main (3e personne, monde) et une à la caméra (1ère personne).
      const addHeld = (thirdChild: THREE.Object3D, fpChild: THREE.Object3D) => {
        const g3 = new THREE.Group();
        g3.position.set(HOLD3.pos[0], HOLD3.pos[1], HOLD3.pos[2]);
        g3.rotation.set(HOLD3.rot[0], HOLD3.rot[1], HOLD3.rot[2]);
        g3.add(thirdChild);
        root.add(g3);
        heldThirdRef.current = g3;

        const gf = new THREE.Group();
        gf.position.set(HOLDFP.pos[0], HOLDFP.pos[1], HOLDFP.pos[2]);
        gf.rotation.set(HOLDFP.rot[0], HOLDFP.rot[1], HOLDFP.rot[2]);
        gf.add(fpChild);
        camPovRef.current?.add(gf);
        heldFpRef.current = gf;
      };

      const slot = attachable?.texture ? detectArmorSlot(attachable.geometry, identifier) : null;

      // 1) Armure équipée sur le corps (en plus de l'item tenu, jamais à la place).
      if (slot && attachable?.texture) {
        // Géo du pack résolue (héritage 1.8 + reset gérés côté backend) si dispo,
        // sinon la géo vanilla reconstruite. Couche par-dessus le corps.
        const armorImg = await loadPackImage(groupId, attachable.texture);
        if (!cancelled && armorImg) {
          let entry: any = null;
          if (attachable.geometry) {
            entry = (await apiService.getPackGeometry(groupId, attachable.geometry)).data;
          }
          if (!entry || !(entry.bones?.length)) entry = armorGeometry(slot);
          if (!cancelled) root.add(buildGeometryMesh(entry, armorImg, 'alpha_test', false));
        }
      }

      // 2) Item tenu (toujours, y compris pour les armures) : modèle 3D si l'attachable
      //    en fournit un (item non-armure), sinon l'icône plate.
      if (!slot && attachable?.geometryFile) {
        const entry = attachable.geometry ? (await apiService.getPackGeometry(groupId, attachable.geometry)).data : null;
        const tex = attachable.texture ? await loadPackImage(groupId, attachable.texture) : null;
        if (!cancelled && entry && entry.bones?.length && tex) {
          const held = buildGeometryMesh(entry, tex, 'alpha_test', true);
          held.scale.setScalar(0.8);
          addHeld(held, held.clone());
        }
      } else if (iconPath) {
        // Sprite (icône) tenu, transform distinct 3e / 1ère personne comme en jeu.
        const icon = await loadPackImage(groupId, iconPath);
        if (!cancelled && icon) {
          const tex = iconTexture(icon);
          addHeld(flatPlane(tex, HOLD3.size, HOLD3.off), flatPlane(tex, HOLDFP.size));
        }
      }

      if (cancelled) { disposeObject3D(root); if (heldFpRef.current) { camPovRef.current?.remove(heldFpRef.current); disposeObject3D(heldFpRef.current); heldFpRef.current = null; } return; }
      scene.add(root);
      modelRef.current = root;
      setStatus('ready');
    })().catch(() => { if (!cancelled) setStatus('error'); });

    return () => { cancelled = true; };
  }, [groupId, identifier, iconPath, attachable]);

  return (
    <div ref={containerRef} className={styles.viewer}>
      <canvas ref={canvasRef} className={styles.canvas} />
      <div className={styles.povLabelLeft}>3e personne</div>
      <div className={styles.povLabelRight}>Vision joueur</div>
      <div className={styles.povDivider} />
      {status === 'loading' && <div className={styles.overlay}>Chargement du modèle…</div>}
      {status === 'error' && <div className={styles.overlay}>Erreur de rendu</div>}
    </div>
  );
}
